import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSpark } from "@/lib/store";
import { TalkInput } from "@/components/TalkInput";
import { submitStartingPoint } from "@/lib/api";
import { toast } from "sonner";

const REASONS = [
  "I feel stuck and don't know what I want",
  "I'm unhappy in my current role",
  "I think I need a career change",
  "I know I want to pivot",
  "I want to progress or get promoted",
  "I've been made redundant or my role is ending",
  "I've been terminated or left unexpectedly",
  "I'm returning after time away from work",
  "I've lost confidence in myself",
  "I want more meaning or purpose",
  "I have several options and can't decide",
  "I want to explore self-employment or a portfolio career",
  "I'm doing okay but want to be more intentional about my career",
  "Something else",
];

export default function Onboarding() {
  const { userId, setProfile } = useSpark();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({ reasons: [], story: "", hope: "", clarity: 5, confidence: 5 });

  const toggleReason = (r) =>
    setData(d => ({ ...d, reasons: d.reasons.includes(r) ? d.reasons.filter(x => x !== r) : [...d.reasons, r] }));

  const next = () => setStep(s => s + 1);
  const back = () => setStep(s => Math.max(0, s - 1));

  const finish = async () => {
    setSaving(true);
    try {
      const p = await submitStartingPoint(userId, data);
      setProfile(p);
      toast("Spark is drafting your Starting Point…");
      nav("/starting-point");
    } catch (e) {
      toast("Something interrupted us. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-8">
        <div className="h-px flex-1 bg-[rgba(31,27,22,0.1)]" />
        <span className="spark-eyebrow">Starting Point · Step {step + 1} of 4</span>
        <div className="h-px flex-1 bg-[rgba(31,27,22,0.1)]" />
      </div>

      {step === 0 && (
        <div>
          <h1 className="text-4xl sm:text-5xl leading-tight text-[#17171C]">What brought you to Spark?</h1>
          <p className="mt-4 text-lg text-[#56565D]">Choose anything that fits. You can pick more than one.</p>
          <div className="mt-8 flex flex-wrap gap-2.5">
            {REASONS.map(r => (
              <button
                key={r}
                data-testid={`onboarding-reason-${r.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => toggleReason(r)}
                className={`spark-chip ${data.reasons.includes(r) ? "active" : ""}`}
              >
                {r}
              </button>
            ))}
          </div>
          <div className="mt-10 flex justify-between">
            <span />
            <button
              data-testid="onboarding-next-btn"
              onClick={next}
              disabled={data.reasons.length === 0}
              className="spark-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div>
          <h1 className="text-4xl sm:text-5xl leading-tight text-[#17171C]">Tell me a little more about what's happening.</h1>
          <p className="mt-4 text-lg text-[#56565D]">In your own words. There's no right length.</p>
          <TalkInput
            testid="onboarding-story-input"
            rows={8}
            warm
            value={data.story}
            onChange={v => setData({ ...data, story: v })}
            placeholder="You might describe the situation, how it feels, or the question you keep coming back to…"
            className="mt-6"
          />
          <div className="mt-8 flex justify-between">
            <button onClick={back} className="spark-btn-secondary">Back</button>
            <button data-testid="onboarding-next-btn-2" onClick={next} className="spark-btn-primary">Continue</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <h1 className="text-4xl sm:text-5xl leading-tight text-[#17171C]">If Spark really helped you, what would be different by the time you finished?</h1>
          <TalkInput
            testid="onboarding-hope-input"
            rows={6}
            warm
            value={data.hope}
            onChange={v => setData({ ...data, hope: v })}
            placeholder="A feeling, a decision, a plan — whatever comes to mind."
            className="mt-6"
          />
          <div className="mt-8 flex justify-between">
            <button onClick={back} className="spark-btn-secondary">Back</button>
            <button data-testid="onboarding-next-btn-3" onClick={next} className="spark-btn-primary">Continue</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <h1 className="text-4xl sm:text-5xl leading-tight text-[#17171C]">Two quick check-ins.</h1>
          <p className="mt-4 text-lg text-[#56565D]">We'll revisit these later.</p>

          {[
            { key: "clarity", label: "How clear are you about what you want next?" },
            { key: "confidence", label: "How confident are you that you can make it happen?" },
          ].map(s => (
            <div key={s.key} className="mt-10">
              <div className="flex items-baseline justify-between">
                <p className="text-2xl leading-snug text-[#17171C] max-w-xl">{s.label}</p>
                <span className="text-3xl text-[#2C50A0]" data-testid={`onboarding-${s.key}-value`}>{data[s.key]}</span>
              </div>
              <input
                type="range" min={0} max={10} step={1}
                data-testid={`onboarding-${s.key}-slider`}
                value={data[s.key]}
                onChange={e => setData({ ...data, [s.key]: parseInt(e.target.value) })}
                className="mt-4 w-full accent-[#2C50A0]"
              />
              <div className="flex justify-between text-xs text-[#8A8A91] mt-1">
                <span>Not at all</span><span>Completely</span>
              </div>
            </div>
          ))}

          <div className="mt-12 flex justify-between">
            <button onClick={back} className="spark-btn-secondary">Back</button>
            <button
              data-testid="onboarding-finish-btn"
              onClick={finish}
              disabled={saving}
              className="spark-btn-primary disabled:opacity-60"
            >
              {saving ? "Drafting your Starting Point…" : "See my Starting Point"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
